import {put, call} from 'redux-saga/effects';
import DeadlineRule from "../../entities/deadlineRule";
import * as Actions from "../actions/deadlineRuleActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type DeadlineRuleAction = {
    deadlineRuleId: string;
    deadlineRule: DeadlineRule;
    deadlineRules: DeadlineRule[];
};

export function* fetchOneDeadlineRule(action: DeadlineRuleAction) {
	try {
		const deadlineRule: DeadlineRule = yield call(api.findOne, 'deadlineRule', action.deadlineRuleId);

		// Now we can update the user interface
		yield put({type: Actions.DEADLINERULE_FETCH_ONE_SUCCESS, deadlineRuleId: action.deadlineRuleId, deadlineRule});
	}
	catch(err) {
		yield put({type: Actions.DEADLINERULE_FETCH_ONE_FAILURE, deadlineRuleId: action.deadlineRuleId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {DeadlineRuleAction} action
 */
export function* saveDeadlineRule(action: DeadlineRuleAction) {
    // console.debug('⮂ DEADLINERULE_SAVE saga', action.deadlineRule?.id);
    
	if (action.deadlineRule === undefined || action.deadlineRule === null) {
		console.error('saveDeadlineRule received a null');
		throw new Error('saveDeadlineRule received a null');
	}

	if (!(action.deadlineRule instanceof DeadlineRule)) {
		action.deadlineRule = new DeadlineRule(action.deadlineRule);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'deadlineRule', action.deadlineRule);
		if (addResult.isErr()) {
			console.error(`⮂ saveDeadlineRule: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DEADLINERULE_SAVE_FAILURE, deadlineRule: action.deadlineRule, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DEADLINERULE_SAVE, deadlineRule: action.deadlineRule});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'deadlineRule', action.deadlineRule.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.DEADLINERULE_SAVE_FAILURE, deadlineRule: action.deadlineRule, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {DeadlineRuleAction} action
 * @param {DeadlineRule[]} action.deadlineRules
 */
export function* saveManyDeadlineRules(action: DeadlineRuleAction) {
    // console.debug('⮂ DEADLINERULE_SAVE_MANY saga', action.deadlineRules.map(deadlineRule => deadlineRule.id).join(','));
    
    if (!Array.isArray(action.deadlineRules)) {
        console.error('saveManyDeadlineRules expected an array, received', action.deadlineRules);
        throw new Error('saveManyDeadlineRules expected an array');
    }

	try {
		const toSave: DeadlineRule[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.deadlineRules.length; i++) {
			const deadlineRule = action.deadlineRules[i];
			deadlineRule.persisted = false;
			toSave.push(deadlineRule);
		}

		const addResult = yield call(idb.add, 'deadlineRule', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyDeadlineRules: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DEADLINERULE_SAVE_MANY_FAILURE, deadlineRules: action.deadlineRules, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DEADLINERULE_SAVE_MANY, deadlineRules: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.DEADLINERULE_SAVE_MANY_FAILURE, deadlineRules: action.deadlineRules, status: 500, err});

		console.error(`Error saving ${action.deadlineRules.length} deadlineRules to database`, err);
	}
}

/**
 * @param {DeadlineRuleAction} action
 */
export function* removeDeadlineRule(action: DeadlineRuleAction) {
    const userId = getCurrentUserId();
	if (!userId || action.deadlineRule === undefined || action.deadlineRule === null || !(action.deadlineRule instanceof DeadlineRule)) {
			console.error('removeDeadlineRule expected type DeadlineRule, received', action.deadlineRule);
			throw new Error('removeDeadlineRule expected type DeadlineRule');
		}

	try {
		// Mark as deleted
		action.deadlineRule.status = 'D';
		action.deadlineRule.addedOn = newHRTimestamp();
		action.deadlineRule.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'deadlineRule', action.deadlineRule);
		if (addResult.isErr()) {
			console.error(`⮂ removeDeadlineRule: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DEADLINERULE_REMOVE_FAILURE, deadlineRule: action.deadlineRule, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DEADLINERULE_REMOVE, deadlineRule: action.deadlineRule});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.DEADLINERULE_REMOVE_FAILURE, deadlineRule: action.deadlineRule, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this DeadlineRule.
	*
	* @param {DeadlineRuleAction} action
	*/
export function* restoreDeadlineRule(action: DeadlineRuleAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreDeadlineRule expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.deadlineRule === undefined || action.deadlineRule === null) {
        console.error('restoreDeadlineRule expected a DeadlineRule, received null');
        throw new Error('restoreDeadlineRule expected a DeadlineRule, received null');
    }
    else if (!(action.deadlineRule instanceof DeadlineRule)) action.deadlineRule = new DeadlineRule(action.deadlineRule); 
    
	try {
		// Mark as restored
		action.deadlineRule.status = 'U';
		action.deadlineRule.addedOn = newHRTimestamp();
		action.deadlineRule.addedBy = userId;
		action.deadlineRule.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'deadlineRule', action.deadlineRule);
		if (addResult.isErr()) {
			console.error(`⮂ restoreDeadlineRule: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DEADLINERULE_RESTORE_FAILURE, deadlineRule: action.deadlineRule, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DEADLINERULE_RESTORE, deadlineRule: action.deadlineRule});
	}
	catch(err) {
		yield put({type: Actions.DEADLINERULE_RESTORE_FAILURE, deadlineRule: action.deadlineRule, status: 500, err});

		console.error('Error restoring from database', err);
	}
}